import styled, { css } from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner } from '@entities/user';
import { useProgressPermission } from '@entities/progress';
import {
  useGetProgressMetricsQuery,
  useListProgressInstructionsQuery,
  useListProgressObjectsQuery,
} from '@shared/api';
import { blockDrop, popIn, riseIn } from '@shared/theme/animations';
import { useCountUp } from '@shared/lib/useCountUp';
import { IconBarChart, IconSparkles } from '@shared/ui/icons';

/* Премиальная оболочка агента ОСНОВА.Руководитель — «командный мостик».
   В отличие от остальных агентов (левый рельс a-la Mto) — hero-баннер «жидкое
   стекло»: полупрозрачный, с терракотовой подсветкой, эмблемой, живыми KPI и
   сегментированной навигацией.
   Hero держит идентичность агента на всех под-роутах (обзор / реестр / карточка),
   подчёркивая, что это управленческий, «надстроечный» агент над всеми системами. */

const Shell = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

/* ── Hero ───────────────────────────────────────────────────────────────── */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.xl};
  /* Жидкое стекло: полупрозрачный терракотовый frosted-слой поверх страницы —
     «максимально прозрачный», идентичность держит лёгкий тинт, объём — грань и
     свечение. Работает в обеих темах (текст переведён на theme-токены: белый по
     прозрачному стеклу не читался бы в светлой теме). */
  background: linear-gradient(
    135deg,
    rgba(${({ theme }) => theme.colors.tint.base}, 0.16) 0%,
    rgba(${({ theme }) => theme.colors.tint.base}, 0.09) 52%,
    rgba(${({ theme }) => theme.colors.tint.glow}, 0.11) 100%
  );
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1px solid rgba(${({ theme }) => theme.colors.tint.glow}, 0.3);
  /* Глубина — нейтральная тень; сверху лёгкий терракотовый ореол + верхний блик стекла. */
  box-shadow:
    0 16px 40px -18px rgba(0, 0, 0, 0.45),
    0 0 34px -22px rgba(${({ theme }) => theme.colors.tint.glow}, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  padding: ${({ theme }) => `${theme.spacing(7)} ${theme.spacing(8)}`};
  animation: ${riseIn} 460ms ${({ theme }) => theme.motion.easeOut} both;

  /* Терракотовый «свет из-за стекла»: мягкий статичный bloom в двух углах.
     Без анимации — сдвиг слоя оголял край и давал «пустое пространство». */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(80% 130% at 85% -20%, rgba(${({ theme }) => theme.colors.tint.glow}, 0.2) 0%, rgba(${({ theme }) => theme.colors.tint.glow}, 0) 60%),
      radial-gradient(70% 130% at 8% 120%, rgba(${({ theme }) => theme.colors.tint.base}, 0.14) 0%, rgba(${({ theme }) => theme.colors.tint.base}, 0) 55%);
    pointer-events: none;
  }

  /* Тонкая терракотовая светящаяся кромка снизу — акцент подсветки «жидкого стекла». */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(${({ theme }) => theme.colors.tint.base}, 0) 0%,
      rgba(${({ theme }) => theme.colors.tint.glow}, 0.65) 50%,
      rgba(${({ theme }) => theme.colors.tint.base}, 0) 100%
    );
    box-shadow: 0 0 8px 0 rgba(${({ theme }) => theme.colors.tint.glow}, 0.3);
    pointer-events: none;
  }

  @media (max-width: 760px) {
    padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)}`};
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(6)};
  flex-wrap: wrap;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  min-width: 0;
`;

const Emblem = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(${({ theme }) => theme.colors.tint.base}, 0.1);
  border: 1px solid rgba(${({ theme }) => theme.colors.tint.glow}, 0.3);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 0 14px -8px rgba(${({ theme }) => theme.colors.tint.glow}, 0.32);
  transform-origin: bottom center;
  animation: ${blockDrop} 540ms ${({ theme }) => theme.motion.spring} both;

  @media (max-width: 760px) {
    width: 48px;
    height: 48px;
  }
`;

const Titles = styled.div`
  min-width: 0;
`;

const Wordmark = styled.h1`
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 760px) {
    font-size: 22px;
  }
`;

/* Одна буква воркмарка — «падает блоком» с задержкой по индексу (паттерн Brand). */
const Letter = styled.span<{ $i: number; $accent: boolean }>`
  display: inline-block;
  transform-origin: bottom center;
  color: ${({ $accent, theme }) => ($accent ? theme.colors.primary : theme.colors.text.primary)};
  font-weight: ${({ $accent }) => ($accent ? 800 : 700)};
  animation: ${blockDrop} 480ms ${({ theme }) => theme.motion.spring} both;
  animation-delay: ${({ $i }) => `${$i * 38}ms`};
`;

const Tagline = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 520px;
  animation: ${riseIn} 460ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: 520ms;
`;

const StatStrip = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2.5)};
  flex-wrap: wrap;
`;

const Chip = styled.div<{ $delay: number }>`
  min-width: 96px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: rgba(${({ theme }) => theme.colors.tint.base}, 0.08);
  border: 1px solid rgba(${({ theme }) => theme.colors.tint.glow}, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px) saturate(150%);
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
`;

const ChipValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

const ChipLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ── Segmented nav ──────────────────────────────────────────────────────── */

const Nav = styled.nav`
  display: inline-flex;
  align-self: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
`;

const Seg = styled(Link)<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  white-space: nowrap;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.backgroundElevated};
          color: ${theme.colors.primary};
          box-shadow: ${theme.shadow.sm};
          animation: ${popIn} 220ms ${theme.motion.spring} both;
        `
      : css`
          background: transparent;
          color: ${theme.colors.text.secondary};
          &:hover {
            color: ${theme.colors.text.primary};
          }
        `}

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const WORDMARK_PREFIX = 'ОСНОВА';
const WORDMARK_ACCENT = '.Руководитель';
const WORDMARK_TEXT = WORDMARK_PREFIX + WORDMARK_ACCENT;

/** Воркмарк, собранный по буквам для побуквенной анимации «падения блоков».
 *  Буквы скрыты от скринридера — он читает `aria-label` контейнера. */
function AnimatedWordmark() {
  const chars = [
    ...WORDMARK_PREFIX.split('').map((ch) => ({ ch, accent: false })),
    ...WORDMARK_ACCENT.split('').map((ch) => ({ ch, accent: true })),
  ];
  return (
    <>
      {chars.map((c, i) => (
        <Letter key={i} $i={i} $accent={c.accent} aria-hidden>
          {c.ch}
        </Letter>
      ))}
    </>
  );
}

/** Число KPI-чипа с анимацией count-up (0 → значение). */
function ChipNumber({ value }: { value: number }) {
  const n = useCountUp(value);
  return <>{Math.round(n).toLocaleString('ru-RU')}</>;
}

/** KPI-чипы шапки: объекты, поручения в работе и просроченные, сводные отчёты. */
type StatKey = 'objects' | 'instructions_open' | 'instructions_overdue' | 'runs_total';
const STATS: ReadonlyArray<{ key: StatKey; label: string }> = [
  { key: 'objects', label: 'Объектов' },
  { key: 'instructions_open', label: 'Поручений в работе' },
  { key: 'instructions_overdue', label: 'Просрочено' },
  { key: 'runs_total', label: 'Отчётов' },
];

/**
 * Премиальная оболочка агента ОСНОВА.Руководитель. Hero-баннер (идентичность +
 * живая сводка) держится на всех под-роутах; ниже — сегментированная навигация
 * «Дашборд объекта» / «Поручения» / «Эксперт» / «Сводные отчёты» и `<Outlet/>`
 * («Эксперт» уводит на отдельную полноэкранную страницу `/expert`). На форме
 * запуска, реестре и карточке отчёта активна вкладка «Сводные отчёты».
 */
export function ProgressLayout() {
  const location = useLocation();
  const { can_read, can_write } = useProgressPermission();
  const { data: metrics, isSuccess } = useGetProgressMetricsQuery(undefined, { skip: !can_read });
  const { data: objects, isSuccess: objectsReady } = useListProgressObjectsQuery(undefined, {
    skip: !can_read,
  });
  const { data: openInstructions, isSuccess: instructionsReady } = useListProgressInstructionsQuery(
    { status: 'issued' },
    { skip: !can_read },
  );

  const segments = location.pathname.split('/').filter(Boolean); // ['agents','progress',...]
  const section = segments[2];
  const isOverview = segments.length === 2;
  const onReports = section === 'reports' || section === 'runs';
  const onInstructions = section === 'instructions';

  function statValue(key: StatKey): number | null {
    if (key === 'objects') return objectsReady && objects ? objects.length : null;
    if (key === 'instructions_open') {
      return instructionsReady && openInstructions ? openInstructions.length : null;
    }
    if (key === 'instructions_overdue') {
      return instructionsReady && openInstructions
        ? openInstructions.filter((i) => i.overdue).length
        : null;
    }
    return isSuccess && metrics ? metrics[key] : null;
  }

  return (
    <Shell>
      <Hero aria-label="ОСНОВА.Руководитель">
        <HeroInner>
          <Identity>
            <Emblem aria-hidden>
              <IconBarChart size={26} />
            </Emblem>
            <Titles>
              <Wordmark aria-label={WORDMARK_TEXT}>
                <AnimatedWordmark />
              </Wordmark>
              <Tagline>
                Всё по объекту на одном экране — переписка с Заказчиком, претензии, прогресс,
                выполнено и запроцентовано, план и факт КСП. Только чтение: источник истины остаётся
                в агентах ОСНОВЫ.
              </Tagline>
            </Titles>
          </Identity>
          <StatStrip aria-label="Сводка по объектам, поручениям и отчётам">
            {STATS.map((s, i) => {
              const v = statValue(s.key);
              return (
                <Chip key={s.key} $delay={560 + i * 90}>
                  <ChipValue>{v !== null ? <ChipNumber value={v} /> : '—'}</ChipValue>
                  <ChipLabel>{s.label}</ChipLabel>
                </Chip>
              );
            })}
          </StatStrip>
        </HeroInner>
      </Hero>

      <Nav aria-label="Разделы агента Руководитель">
        <Seg
          to="/agents/progress"
          $active={isOverview}
          aria-current={isOverview ? 'page' : undefined}
        >
          Дашборд объекта
        </Seg>
        <Seg
          to="/agents/progress/instructions"
          $active={onInstructions}
          aria-current={onInstructions ? 'page' : undefined}
        >
          Поручения
        </Seg>
        {/* Эксперт открывается отдельной страницей во весь экран; её кнопка
            «Вернуться в ОСНОВУ» приведёт обратно сюда. */}
        <Seg
          to="/expert"
          state={{ from: location.pathname + location.search }}
          $active={false}
          title="ИИ-чат руководителя — откроется отдельной страницей"
        >
          Эксперт <IconSparkles size={14} />
        </Seg>
        <Seg
          to="/agents/progress/reports"
          $active={onReports}
          aria-current={onReports ? 'page' : undefined}
        >
          Сводные отчёты
        </Seg>
        <Seg
          to="/agents/ceo"
          $active={false}
          title="Перейти в консоль Генерального директора (банки, платежи, кредиты и лизинги, БДДС / КС-3)"
        >
          Консоль Гендиректора
        </Seg>
      </Nav>

      <Main>
        {can_read && !can_write && <ReadOnlyBanner />}
        <Outlet />
      </Main>
    </Shell>
  );
}
